import * as bowser from 'bowser';
import moment, { MomentInput } from 'moment';
import { timer } from 'rxjs';
import { BlockOptions } from './blocked.directive';
import { Helpers } from './helpers';

export namespace UIHelpers {

  // eslint-disable-next-line no-undef
  declare interface BlockableDocument extends JQueryStatic {
    blockUI(options?: BlockOptions): void;
    unblockUI(): void;
  }

  export function block(message: string = 'Loading...', css: string = null): void {
    ($ as BlockableDocument).blockUI({ message, css: Helpers.tryParseJSON(css) });
  }

  export function unblock(): void {
    ($ as BlockableDocument).unblockUI();
  }

  export function toggleSideMenu(sideMenuId: string): void {
    // Close any other drop downs, and open the current one.
    $('.sideMenu').each((i: number, el: HTMLElement) => {
      if (el.id === sideMenuId || el.classList.contains('sideMenuVisible')) {
        el.toggleAttribute('hidden');
        el.classList.toggle('sideMenuVisible');
      }
    });
  }

  export function closeSideMenu(event: any, followLink: boolean): boolean {
    $('.sideMenu').each((i: number, el: HTMLElement) => {
      el.toggleAttribute('hidden');
    });
    $('.sideMenuVisible').toggleClass('sideMenuVisible');

    return followLink;
  }

  export function handlePanelChanged(panelId, show, currentPaneId: string): string {
    if (show) {
      UIHelpers.toggleActive(currentPaneId, panelId);
      return panelId;
    }
    if (panelId === currentPaneId) {
      UIHelpers.toggleActive(currentPaneId);
      return '';
    }
    return null;
  }

  // Bootstrap deprecated active class on panel headers but we depend on this.
  export function toggleActive(prevId?: string, currentId?: string): void {
    if (prevId) {
      $(`#${prevId}-header`).toggleClass('active');
    }
    if (currentId) {
      $(`#${currentId}-header`).toggleClass('active');
    }
  }

  export function resetSideMenu(): void {
    $('header,main').trigger('click', () => {
      $('.primaryContainer').removeClass('showSideMenu');
    });
  }

  export function getSideMenuPrefix(panel: string = 'productsSideMenu'): string {
    let prefix = '';
    switch (panel) {
    case 'schoolsSideMenu':
      prefix = 'p_sch_';
      break;
    case 'districtsSideMenu':
      prefix = 'p_dis_';
      break;
    case 'childrenSideMenu':
      prefix = 'p_chi_';
      break;
    default:
      prefix = 'p_pl_';
    }
    return prefix;
  }

  export function isSideMenuClosed(id: string, activePaneId: string, panel: string = 'productsSideMenu'): boolean {
    const prefix = getSideMenuPrefix(panel);
    return activePaneId !== `${prefix}${id}`;
  }

  export function isSideMenuOpen(id: string | number, activePaneId: string, panel: string = 'productsSideMenu'): boolean {
    const prefix = getSideMenuPrefix(panel);
    return activePaneId === `${prefix}${id}`;
  }

  /** checks tinymce editor for the selection and toggles the style and returns the state for it. */
  export function toggleEditorSelection(styleName: string, editor: any, options?: any): boolean {
    // Only check for object properties that are in the allowed styles.
    const applied = editor.formatter.match(styleName, options);
    const selectionLength: number = editor.selection.getContent().length;

    if (applied) {
      editor.formatter.remove(styleName, { value: null }, null, true);
    } else {
      editor.formatter.apply(styleName, options);
    }

    // Returns whether a match was found.
    return selectionLength === 0;
  }

  export function getMMDDYYYYDateWithSlashes(date: MomentInput) : any {
    if (date) {
      return moment.utc(date).format('MM/DD/YYYY');
    }

    return null;
  }

  export function getYYYYMMDDDateWithDashes(date: MomentInput) : any {
    if (date) {
      return moment.utc(date).format('YYYY-MM-DD');
    }

    return null;
  }

  export function isInsideIFrame(): boolean {
    return window.self !== window.top
    && window.parent
    && window.self.location.hostname === window.parent.location.hostname;
  }

   /**
   * Go back to ZB Portal via browser history, iframe or origin if no history to go back to.
   *
   * Allow a bypass to close the window if the user came in from launching a new window.
   *
   * A student should always be closing out via iframe or origin.
   * A non-student, in most use cases, will have launched in a new window, so needs to use the bypass.
   * A teacher reviewing a writing activity will close out sing browser history as the other cases do not apply.
   *
   * Browser navigation or origin switching will not work reliably when running Angular dev servers for ZB Portal and
   * Digital-Resources simultaneously because an extra navigation is required to proceed between the two, but this will
   * work in a full environment when there is one navigation step from ZB Portal to Digital-Resources.
   */
  export function goBackToPortal(shouldClose: boolean = false): void {
    if (shouldClose) {
      // Bypass navigation below to close window (as close window).
      UIHelpers.closeWindow(false);
    } else if (isInsideIFrame()) {
      // Digital-Resources sends message to SSE so it will reload the activity page.
      window.parent?.postMessage({ type: 'ACTIVITY_COMPLETE' });
    } else if (window.history.length > 1) {
      // Try with navigation if there is navigation history. This won't work on local development enivronments.
      window.history.back();
    } else {
      // As a fallback, navigate back to the origin of ZBPortal.
      window.location.href = window.location.origin;
    }
  }

  /**
   * Closes window or navigates to origin.
   *
   * Note the new student experience app loads all Digital-Resources activities in an iframe for students only.
   * This means the logic below is problematic for those users.
   */
  export function closeWindow(isStudent: boolean): void {
    if (isStudent) {
      window.location.href = window.location.origin;
    } else {
      const parser = bowser.getParser(window.navigator.userAgent);
      if (parser.isBrowser('msie') || parser.isBrowser('edge')) {
        const wndw = window.self;
        wndw.open('', '_self', '');
        wndw.close();
      } else {
        // This is a hack to work around browser window.close restrictions.
        // Will only work if this window was opened via popup
        const wndw = window.open('', '_self');
        wndw.document.write('');
        timer(100).subscribe(() => {
          wndw.close();
        });
      }
    }
  }
}
